// ui/components/CapacitacionForm.jsx  (presentacional: react-hook-form + yup, sin Api ni Swal)
// `children` = secciones extra (asistentes, imágenes) que se renderizan entre la tarjeta de datos y el pie.
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactHookFormInput } from '../../../../../nuup/componentes/Forms/ReactHookFormInput.jsx';
import CatalogAutocomplete from '../../../shared/ui/CatalogAutocomplete.jsx';
import CatalogMultiSelect from '../../../shared/ui/CatalogMultiSelect.jsx';
import GuardarRegistroButton from '../../../shared/ui/GuardarRegistroButton.jsx';
import PillButton from '../../../shared/ui/PillButton.jsx';
import SectionCard from '../../../shared/ui/SectionCard.jsx';
import { capacitacionSchema, MAX_NOMBRE_CAPACITACION } from '../../domain/capacitacion.schema';

const FIELDSET_STYLE = { border: 0, padding: 0, margin: 0, minWidth: 0 };

// onSubmit(values, setError): la página mapea los errores del backend sobre los campos con setError.
const CapacitacionForm = ({
  initialValues,
  catalogos,
  submitting = false,
  onSubmit,
  onCancel,
  onMunicipioChange,
  validateExtra,
  children,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(capacitacionSchema),
    mode: 'onBlur',
    defaultValues: initialValues,
  });

  const idMunicipio = watch('id_municipio');

  const handleMunicipio = id => {
    setValue('id_localidad', null);
    onMunicipioChange(id);
  };

  // validateExtra() corre siempre (aunque yup falle) para mostrar todos los errores a la vez; si devuelve false no se guarda.
  const submit = event => {
    const extraOk = validateExtra ? validateExtra() : true;
    return handleSubmit(values => (extraOk ? onSubmit(values, setError) : undefined))(event);
  };

  return (
    <form onSubmit={submit} noValidate>
      <fieldset disabled={submitting} style={FIELDSET_STYLE}>
        <SectionCard>
          <div className="row mb-3">
            <div className="col-md-6">
              <ReactHookFormInput
                type="text"
                id="nombre_capacitacion"
                name="nombre_capacitacion"
                label="Nombre de capacitación*"
                control={control}
                error={errors.nombre_capacitacion}
                maxLength={MAX_NOMBRE_CAPACITACION}
              />
            </div>
            <div className="col-md-6">
              <CatalogMultiSelect
                id="id_actividades"
                name="id_actividades"
                label="Actividades*"
                placeholder="Selecciona una o más actividades"
                control={control}
                options={catalogos.actividades}
                error={errors.id_actividades}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <ReactHookFormInput
                type="text"
                id="responsable"
                name="responsable"
                label="Capacitador / responsable*"
                control={control}
                error={errors.responsable}
              />
            </div>
            <div className="col-md-6">
              <ReactHookFormInput type="date" id="fecha" name="fecha" label="Fecha*" control={control} error={errors.fecha} />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <CatalogAutocomplete
                name="id_municipio"
                label="Municipio"
                control={control}
                options={catalogos.municipios}
                onChangeExternal={handleMunicipio}
                startIcon="fa-building"
                placeholder="Busca un municipio"
                error={errors.id_municipio}
              />
            </div>
            <div className="col-md-6">
              <CatalogAutocomplete
                name="id_localidad"
                label="Localidad"
                control={control}
                options={catalogos.localidades}
                loading={catalogos.localidadesLoading}
                disabled={!idMunicipio}
                placeholder={idMunicipio ? 'Busca una localidad' : 'Selecciona primero un municipio'}
                error={errors.id_localidad}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-12">
              <ReactHookFormInput
                type="text"
                id="descripcion"
                name="descripcion"
                label="Descripción (puede incluir objetivos o contexto de la capacitación)"
                control={control}
                multiline
                minRows={3}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <ReactHookFormInput
                type="text"
                id="materiales"
                name="materiales"
                label="Materiales utilizados"
                control={control}
                error={errors.materiales}
              />
            </div>
          </div>
        </SectionCard>

        {children}
      </fieldset>

      <div className="d-flex justify-content-end mb-4" style={{ gap: 10 }}>
        <PillButton onClick={onCancel} disabled={submitting}>
          Cancelar
        </PillButton>
        <GuardarRegistroButton saving={submitting} onClick={submit} />
      </div>
    </form>
  );
};

export default CapacitacionForm;
